import { quote } from '@/state/quote';
import { useAtomValue } from 'jotai';
import { CoReplacementQuestionnaire } from '@/state/types';

const yesNoLabel = (value?: boolean | string | null) => {
  if (value === true || value === 'YES') {
    return 'Yes';
  }
  if (value === false || value === 'NO') {
    return 'No';
  }
  return null;
};

export function CoReplacementReason() {
  const { stateSpecificQuestions, product } = useAtomValue(quote);
  if (product !== 'Accident') {
    return null;
  }
  const responses = (stateSpecificQuestions as Record<string, unknown>)[
    'coReplacementQuestionnaire'
  ] as CoReplacementQuestionnaire;
  const { statementToApplicant } = responses;
  const reasons = [
    'Additional benefits',
    'No change in benefits, but lower premiums',
    'Fewer benefits and lower premiums',
  ];
  const selectedReason = reasons.find(
    (reason) => reason.toLowerCase() === statementToApplicant?.toLowerCase()
  );
  const otherReason = selectedReason ? undefined : statementToApplicant;

  return (
    <div>
      {reasons.map((reason) => (
        <p key={reason}>
          {selectedReason === reason ? 'X' : '_'} {reason}
        </p>
      ))}
      <p>{otherReason ? 'X' : '_'} Other (Please specify.)</p>
      <p className="underline">{otherReason}</p>
    </div>
  );
}

export function ColoradoPdfContent() {
  const { stateSpecificQuestions, product } = useAtomValue(quote);
  const hasAccident = product === 'Accident';
  const responses = (stateSpecificQuestions as Record<string, unknown>)[
    'coReplacementQuestionnaire'
  ] as CoReplacementQuestionnaire;

  return (
    <div>
      <h4>To the best of your knowledge:</h4>
      <dl>
        <dt>
          a. Do you have another health insurance policy or contract in force?
        </dt>
        <dd>{yesNoLabel(responses.hasAnotherHealthPolicy)}</dd>
        {responses.hasAnotherHealthPolicy === true && (
          <>
            <dt>(1) If so, with which company?</dt>
            <dd>{responses.otherHealthInsuranceCompany}</dd>
            <dt>
              (2) If so, do you intend to replace your current health insurance
              policy or contract with this policy?
            </dt>
            <dd>{yesNoLabel(responses.intentToReplaceCurrentCoverage)}</dd>
          </>
        )}
        {hasAccident && (
          <>
            <dt>
              b. Do you have any other health insurance that provides benefits
              similar to this accident and sickness policy?
            </dt>
            <dd>{yesNoLabel(responses.otherAccidentSicknessPolicy)}</dd>
            {responses.otherAccidentSicknessPolicyCompany && (
              <>
                <dt>(1) If so, with which company?</dt>
                <dd>{responses.otherAccidentSicknessPolicyCompany}</dd>
                <dt>(2) What kind of policy?</dt>
                <dd>{responses.otherPolicyType}</dd>
              </>
            )}
          </>
        )}
        <dt>
          {hasAccident ? 'c.' : 'b.'} Are you covered for medical assistance
          through the state Medicaid program, Health First Colorado?
        </dt>
        <dd>{yesNoLabel(responses.medicaidCoverage)}</dd>
        {responses.medicaidCoverage === true && (
          <>
            <dt>(1) As a Specified Low-Income Medicare Beneficiary (SLMB)?</dt>
            <dd>{yesNoLabel(responses.slmb)}</dd>
            <dt>(2) As a Qualified Medicare Beneficiary (QMB)?</dt>
            <dd>{yesNoLabel(responses.qmb)}</dd>
            <dt>(3) For other Medicaid medical benefits?</dt>
            <dd>{yesNoLabel(responses.otherMedicaidBenefits)}</dd>
          </>
        )}
      </dl>
    </div>
  );
}
